import React, { useEffect, useMemo, useState } from "react";
import api, { formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Loader2,
  Plus,
  Trash2,
  Truck,
  ArrowRight,
  Check,
  RotateCcw,
  Pencil,
  Package,
} from "lucide-react";

function timeAgo(iso) {
  if (!iso) return "";
  try {
    const then = new Date(iso).getTime();
    const now = Date.now();
    const min = Math.max(0, Math.round((now - then) / 60000));
    if (min < 1) return "hace instantes";
    if (min < 60) return `hace ${min} min`;
    const hr = Math.round(min / 60);
    if (hr < 24) return `hace ${hr} h`;
    const d = Math.round(hr / 24);
    return `hace ${d} d`;
  } catch {
    return "";
  }
}

export default function Transportes() {
  const [items, setItems] = useState([]);
  const [sucursales, setSucursales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    item_name: "",
    from_sucursal_id: "",
    to_sucursal_id: "",
    quantity: "",
    note: "",
  });

  const load = async () => {
    try {
      const [tr, sc] = await Promise.all([
        api.get("/transportes"),
        api.get("/sucursales"),
      ]);
      setItems(tr.data);
      setSucursales(sc.data);
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || "Error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // Refresh once per minute so "hace X min" stays fresh and delivered items
    // older than 24h eventually drop off.
    const t = setInterval(load, 60_000);
    return () => clearInterval(t);
  }, []);

  const openNew = () => {
    setEditing(null);
    setForm({
      item_name: "",
      from_sucursal_id: sucursales[0]?.id || "",
      to_sucursal_id: sucursales[1]?.id || sucursales[0]?.id || "",
      quantity: "",
      note: "",
    });
    setOpen(true);
  };

  const openEdit = (it) => {
    setEditing(it);
    setForm({
      item_name: it.item_name || "",
      from_sucursal_id: it.from_sucursal_id || "",
      to_sucursal_id: it.to_sucursal_id || "",
      quantity: it.quantity ?? "",
      note: it.note || "",
    });
    setOpen(true);
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!form.item_name.trim()) {
      toast.error("Escribe qué se transporta");
      return;
    }
    if (!form.from_sucursal_id || !form.to_sucursal_id) {
      toast.error("Selecciona origen y destino");
      return;
    }
    if (form.from_sucursal_id === form.to_sucursal_id) {
      toast.error("Origen y destino deben ser distintos");
      return;
    }
    setSaving(true);
    const payload = {
      item_name: form.item_name.trim(),
      from_sucursal_id: form.from_sucursal_id,
      to_sucursal_id: form.to_sucursal_id,
      quantity:
        form.quantity === "" || form.quantity === null
          ? null
          : Number(form.quantity),
      note: form.note?.trim() || null,
    };
    try {
      if (editing) {
        const { data } = await api.patch(`/transportes/${editing.id}`, payload);
        setItems((prev) => prev.map((x) => (x.id === data.id ? data : x)));
        toast.success("Transporte actualizado");
      } else {
        const { data } = await api.post("/transportes", payload);
        setItems((prev) => [data, ...prev]);
        toast.success("Transporte agregado");
      }
      setOpen(false);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Error");
    } finally {
      setSaving(false);
    }
  };

  const togglePickup = async (it) => {
    try {
      const { data } = await api.post(`/transportes/${it.id}/pickup`);
      setItems((prev) => prev.map((x) => (x.id === data.id ? data : x)));
      toast.success(
        data.picked_up ? "Marcado como recogido" : "Recogido desmarcado"
      );
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || "Error");
    }
  };

  const toggleDeliver = async (it) => {
    try {
      const { data } = await api.post(`/transportes/${it.id}/deliver`);
      setItems((prev) => prev.map((x) => (x.id === data.id ? data : x)));
      toast.success(
        data.delivered ? "Marcado como entregado" : "Marcado como pendiente"
      );
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || "Error");
    }
  };

  const remove = async (it) => {
    if (!window.confirm(`¿Eliminar el transporte de "${it.item_name}"?`)) return;
    try {
      await api.delete(`/transportes/${it.id}`);
      setItems((prev) => prev.filter((x) => x.id !== it.id));
      toast.success("Transporte eliminado");
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || "Error");
    }
  };

  // Group pending transportes by route key "from → to"
  const grouped = useMemo(() => {
    const pending = items.filter((i) => !i.delivered);
    const delivered = items.filter((i) => i.delivered);
    const groups = {};
    for (const it of pending) {
      const key = `${it.from_sucursal_id}__${it.to_sucursal_id}`;
      if (!groups[key]) {
        groups[key] = {
          key,
          from: it.from_sucursal,
          to: it.to_sucursal,
          items: [],
        };
      }
      groups[key].items.push(it);
    }
    // Within each group, put "picked_up" (in transit) first
    for (const g of Object.values(groups)) {
      g.items.sort((a, b) => (b.picked_up ? 1 : 0) - (a.picked_up ? 1 : 0));
    }
    return { groups: Object.values(groups), delivered };
  }, [items]);

  const pendingCount = items.filter((i) => !i.delivered).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-[#FF7878]" />
      </div>
    );
  }

  if (sucursales.length < 2) {
    return (
      <div className="space-y-6">
        <header>
          <h1
            className="font-display text-3xl sm:text-4xl font-black tracking-tight text-[#4A3B32]"
            data-testid="transportes-title"
          >
            Transporte entre sucursales
          </h1>
        </header>
        <div
          className="bg-white border border-dashed border-[#EADBCE] rounded-3xl py-16 text-center"
          data-testid="transportes-need-sucursales"
        >
          <Truck className="mx-auto w-12 h-12 text-[#EADBCE]" />
          <h3 className="mt-4 font-display font-bold text-lg text-[#4A3B32]">
            Necesitas al menos 2 sucursales
          </h3>
          <p className="text-sm text-[#8A796F] mt-1">
            Agrega otra sucursal en Configuración → Sucursales para empezar a
            registrar transportes.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1
            className="font-display text-3xl sm:text-4xl font-black tracking-tight text-[#4A3B32]"
            data-testid="transportes-title"
          >
            Transporte entre sucursales
          </h1>
          <p className="text-[#8A796F] mt-1">
            {pendingCount === 0
              ? "Nada pendiente por transportar."
              : `${pendingCount} pendiente${pendingCount === 1 ? "" : "s"} por entregar.`}
          </p>
          <p className="text-xs text-[#8A796F] mt-2 flex items-center gap-3 flex-wrap" data-testid="transportes-legend">
            <span className="inline-flex items-center gap-1.5">
              <span className="w-3.5 h-3.5 rounded-full bg-[#FFC978] inline-block" />
              1º recogido
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="w-3.5 h-3.5 rounded-full bg-[#A7E4D3] inline-block" />
              2º entregado
            </span>
          </p>
        </div>
        <Button
          onClick={openNew}
          data-testid="transporte-new-button"
          className="rounded-full bg-[#FF5C5C] hover:bg-[#E64A4A] text-white font-bold px-5 h-11"
        >
          <Plus className="w-4 h-4 mr-2" />
          Agregar transporte
        </Button>
      </header>

      {grouped.groups.length === 0 && grouped.delivered.length === 0 ? (
        <div
          className="bg-white border border-dashed border-[#EADBCE] rounded-3xl py-16 text-center"
          data-testid="transportes-empty"
        >
          <Truck className="mx-auto w-12 h-12 text-[#EADBCE]" />
          <h3 className="mt-4 font-display font-bold text-lg text-[#4A3B32]">
            Sin transportes por ahora
          </h3>
          <p className="text-sm text-[#8A796F] mt-1">
            Toca &quot;Agregar transporte&quot; para registrar un artículo que se está
            moviendo entre sucursales.
          </p>
        </div>
      ) : (
        <div className="space-y-6" data-testid="transportes-list">
          {grouped.groups.map((g) => (
            <section
              key={g.key}
              className="bg-white border border-[#EADBCE] rounded-3xl overflow-hidden"
              data-testid={`transporte-group-${g.key}`}
            >
              <header className="px-5 py-3 bg-[#FCF9F2] border-b border-[#EADBCE] flex items-center gap-2 flex-wrap">
                <span className="inline-flex items-center gap-1.5 font-display font-extrabold text-[#4A3B32]">
                  {g.from?.name || "—"}
                </span>
                <ArrowRight className="w-4 h-4 text-[#FF5C5C]" />
                <span className="inline-flex items-center gap-1.5 font-display font-extrabold text-[#4A3B32]">
                  {g.to?.name || "—"}
                </span>
                <span className="ml-auto text-xs font-semibold text-[#8A796F]">
                  {g.items.length} artículo{g.items.length === 1 ? "" : "s"}
                </span>
              </header>
              <ul className="divide-y divide-[#F1E5D8]">
                {g.items.map((it) => (
                  <li
                    key={it.id}
                    className="flex items-center gap-3 px-4 py-3 sm:px-5"
                    data-testid={`transporte-row-${it.id}`}
                  >
                    <div className="shrink-0 flex items-center gap-1.5">
                      <button
                        type="button"
                        onClick={() => togglePickup(it)}
                        aria-label={
                          it.picked_up
                            ? "Desmarcar recogido"
                            : "Marcar como recogido"
                        }
                        title={it.picked_up ? "Recogido ✓" : "Marcar como recogido"}
                        data-testid={`transporte-pickup-${it.id}`}
                        className={`w-8 h-8 rounded-full border-2 flex items-center justify-center transition-colors ${
                          it.picked_up
                            ? "bg-[#FFC978] border-[#FFC978] hover:bg-[#F0B85F]"
                            : "border-[#EADBCE] hover:border-[#FFC978] hover:bg-[#FFF6E5]"
                        }`}
                      >
                        <Check
                          className={`w-4 h-4 ${
                            it.picked_up ? "text-white" : "text-transparent"
                          }`}
                          strokeWidth={3}
                        />
                      </button>
                      <button
                        type="button"
                        onClick={() => toggleDeliver(it)}
                        disabled={!it.picked_up}
                        aria-label={
                          !it.picked_up
                            ? "Primero marca como recogido"
                            : "Marcar como entregado"
                        }
                        title={
                          !it.picked_up
                            ? "Primero marca como recogido"
                            : "Marcar como entregado"
                        }
                        data-testid={`transporte-check-${it.id}`}
                        className={`w-8 h-8 rounded-full border-2 flex items-center justify-center transition-colors ${
                          !it.picked_up
                            ? "border-[#EADBCE] opacity-40 cursor-not-allowed"
                            : "border-[#EADBCE] hover:border-[#FF5C5C] hover:bg-[#FFF0F0]"
                        }`}
                      >
                        <Check className="w-4 h-4 text-transparent" />
                      </button>
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold text-[#4A3B32] leading-tight truncate">
                        {it.item_name}
                        {it.quantity ? (
                          <span className="ml-2 text-xs font-bold text-[#8A796F] bg-[#FCF9F2] px-2 py-0.5 rounded-full">
                            × {it.quantity}
                          </span>
                        ) : null}
                        {it.picked_up && (
                          <span
                            className="ml-2 text-[10px] font-bold text-white bg-[#FFC978] px-2 py-0.5 rounded-full"
                            data-testid={`transporte-in-transit-${it.id}`}
                          >
                            En camino
                          </span>
                        )}
                      </p>
                      <p className="text-xs text-[#8A796F] mt-0.5 flex flex-wrap items-center gap-x-2">
                        <span>{timeAgo(it.created_at)}</span>
                        {it.created_by_user?.name && (
                          <span>· por {it.created_by_user.name}</span>
                        )}
                        {it.picked_up && it.picked_up_by_user?.name && (
                          <span className="text-[#B8891F]">
                            · recogido {timeAgo(it.picked_up_at)} por{" "}
                            {it.picked_up_by_user.name}
                          </span>
                        )}
                        {it.note && <span className="italic">· {it.note}</span>}
                      </p>
                    </div>
                    <div className="shrink-0 flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => openEdit(it)}
                        aria-label="Editar"
                        data-testid={`transporte-edit-${it.id}`}
                        className="w-9 h-9 rounded-full text-[#8A796F] hover:bg-[#FCF9F2] hover:text-[#4A3B32] inline-flex items-center justify-center"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => remove(it)}
                        aria-label="Eliminar"
                        data-testid={`transporte-delete-${it.id}`}
                        className="w-9 h-9 rounded-full text-[#8A796F] hover:bg-[#FFF0F0] hover:text-[#FF5C5C] inline-flex items-center justify-center"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          ))}

          {grouped.delivered.length > 0 && (
            <section
              className="bg-white/60 border border-[#EADBCE] rounded-3xl overflow-hidden"
              data-testid="transportes-delivered-section"
            >
              <header className="px-5 py-3 bg-[#F1E5D8]/40 border-b border-[#EADBCE] flex items-center gap-2">
                <Package className="w-4 h-4 text-[#8A796F]" />
                <span className="font-display font-extrabold text-[#8A796F]">
                  Entregados (últimas 24 h)
                </span>
                <span className="ml-auto text-xs font-semibold text-[#8A796F]">
                  {grouped.delivered.length}
                </span>
              </header>
              <ul className="divide-y divide-[#F1E5D8]">
                {grouped.delivered.map((it) => (
                  <li
                    key={it.id}
                    className="flex items-center gap-3 px-4 py-3 sm:px-5 opacity-70"
                    data-testid={`transporte-delivered-row-${it.id}`}
                  >
                    <div className="shrink-0 flex items-center gap-1.5">
                      <div
                        aria-label="Recogido"
                        className="w-8 h-8 rounded-full bg-[#FFC978] flex items-center justify-center"
                      >
                        <Check className="w-4 h-4 text-white" strokeWidth={3} />
                      </div>
                      <button
                        type="button"
                        onClick={() => toggleDeliver(it)}
                        aria-label="Marcar como pendiente"
                        data-testid={`transporte-uncheck-${it.id}`}
                        className="w-8 h-8 rounded-full bg-[#A7E4D3] flex items-center justify-center hover:bg-[#8FD8C1] transition-colors"
                      >
                        <Check className="w-4 h-4 text-white" strokeWidth={3} />
                      </button>
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold text-[#8A796F] leading-tight truncate line-through">
                        {it.item_name}
                        {it.quantity ? ` × ${it.quantity}` : ""}
                        <span className="ml-2 no-underline text-xs font-bold text-[#4A3B32]">
                          {it.from_sucursal?.name} → {it.to_sucursal?.name}
                        </span>
                      </p>
                      <p className="text-xs text-[#8A796F] mt-0.5">
                        Entregado {timeAgo(it.delivered_at)}
                        {it.delivered_by_user?.name && (
                          <span> · por {it.delivered_by_user.name}</span>
                        )}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => toggleDeliver(it)}
                      data-testid={`transporte-restore-${it.id}`}
                      className="shrink-0 inline-flex items-center gap-1 px-3 h-9 rounded-full text-xs font-semibold text-[#8A796F] hover:bg-[#FCF9F2] hover:text-[#4A3B32]"
                    >
                      <RotateCcw className="w-3.5 h-3.5" />
                      Deshacer
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="max-w-md bg-white rounded-3xl border-[#EADBCE] max-h-[90vh] overflow-y-auto"
          data-testid="transporte-dialog"
        >
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-[#4A3B32]">
              {editing ? "Editar transporte" : "Nuevo transporte"}
            </DialogTitle>
            <DialogDescription className="text-sm text-[#8A796F]">
              Registra un artículo que se mueve entre sucursales.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4 mt-2">
            <div className="space-y-2">
              <Label className="font-semibold text-[#4A3B32]">
                ¿Qué se transporta?
              </Label>
              <Input
                data-testid="transporte-item-name"
                value={form.item_name}
                onChange={(e) =>
                  setForm((f) => ({ ...f, item_name: e.target.value }))
                }
                placeholder="p. ej. Helado de limón"
                className="h-11 rounded-xl border-[#EADBCE]"
                autoFocus
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="font-semibold text-[#4A3B32]">Origen</Label>
                <Select
                  value={form.from_sucursal_id}
                  onValueChange={(v) =>
                    setForm((f) => ({ ...f, from_sucursal_id: v }))
                  }
                >
                  <SelectTrigger
                    data-testid="transporte-from"
                    className="h-11 rounded-xl border-[#EADBCE]"
                  >
                    <SelectValue placeholder="Elige sucursal" />
                  </SelectTrigger>
                  <SelectContent>
                    {sucursales.map((s) => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="font-semibold text-[#4A3B32]">Destino</Label>
                <Select
                  value={form.to_sucursal_id}
                  onValueChange={(v) =>
                    setForm((f) => ({ ...f, to_sucursal_id: v }))
                  }
                >
                  <SelectTrigger
                    data-testid="transporte-to"
                    className="h-11 rounded-xl border-[#EADBCE]"
                  >
                    <SelectValue placeholder="Elige sucursal" />
                  </SelectTrigger>
                  <SelectContent>
                    {sucursales.map((s) => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="font-semibold text-[#4A3B32]">
                Cantidad{" "}
                <span className="text-xs font-normal text-[#8A796F]">
                  (opcional)
                </span>
              </Label>
              <Input
                data-testid="transporte-quantity"
                type="number"
                min="0"
                value={form.quantity}
                onChange={(e) =>
                  setForm((f) => ({ ...f, quantity: e.target.value }))
                }
                placeholder="p. ej. 3"
                className="h-11 rounded-xl border-[#EADBCE]"
              />
            </div>
            <div className="space-y-2">
              <Label className="font-semibold text-[#4A3B32]">
                Nota{" "}
                <span className="text-xs font-normal text-[#8A796F]">
                  (opcional)
                </span>
              </Label>
              <Input
                data-testid="transporte-note"
                value={form.note}
                onChange={(e) =>
                  setForm((f) => ({ ...f, note: e.target.value }))
                }
                placeholder="p. ej. Sale a las 15:00"
                className="h-11 rounded-xl border-[#EADBCE]"
              />
            </div>
            <DialogFooter className="gap-2 sm:gap-2 pt-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setOpen(false)}
                className="rounded-full"
                data-testid="transporte-cancel"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={saving}
                data-testid="transporte-save"
                className="rounded-full bg-[#FF5C5C] hover:bg-[#E64A4A] text-white font-bold"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : null}
                {editing ? "Guardar" : "Agregar"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
